import React, { ChangeEvent, useEffect, useState } from 'react';
import { IoSend, IoStop } from 'react-icons/io5';
import { MdWarning } from 'react-icons/md';
import { UsageState } from '../hooks/useMessages';

interface ChatInputProps {
  onSubmit: () => void;
  onStop: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  hasConversation: boolean;
  isLoading: boolean;
  onTextareaChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onCompositionStart: () => void;
  onCompositionEnd: () => void;
  usageInfo?: UsageState;
}

// Helper function to format countdown
const formatCountdown = (resetsAt: string | null): string => {
  if (!resetsAt) return '';
  
  const resetTime = new Date(resetsAt).getTime();
  const now = Date.now();
  const diff = resetTime - now;
  
  if (diff <= 0) return 'soon';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  onStop,
  textareaRef,
  hasConversation,
  isLoading,
  onTextareaChange,
  onKeyDown,
  onCompositionStart,
  onCompositionEnd,
  usageInfo,
}) => {
  // Countdown state for real-time update
  const [countdown, setCountdown] = useState<string>('');

  // Update countdown every minute
  useEffect(() => {
    if (!usageInfo?.limitExceeded || !usageInfo?.resetsAt) {
      setCountdown('');
      return;
    }

    // Initial update
    setCountdown(formatCountdown(usageInfo.resetsAt));

    // Update every minute
    const interval = setInterval(() => {
      setCountdown(formatCountdown(usageInfo.resetsAt));
    }, 60000);

    return () => clearInterval(interval);
  }, [usageInfo?.limitExceeded, usageInfo?.resetsAt]);

  // Determine if we should show usage warning (remaining <= 3 and not unlimited)
  const showUsageWarning = usageInfo && 
    !usageInfo.isUnlimited && 
    usageInfo.remaining <= 3 && 
    !usageInfo.limitExceeded;

  // Determine if input should be disabled
  const isDisabled = isLoading || (usageInfo?.limitExceeded ?? false);

  return (
    <div className={`w-full flex-shrink-0 ${hasConversation ? 'border-t border-gray-200 bg-white' : ''}`}>
      <div className="max-w-3xl mx-auto w-full p-4">
        {/* Usage limit exceeded banner */}
        {usageInfo?.limitExceeded && (
          <div className="mb-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <MdWarning className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">
                Daily limit reached
              </p>
              <p className="text-xs text-red-600">
                You have used all {usageInfo.limit} questions for today. 
                {countdown && ` Resets in ${countdown}.`}
              </p>
            </div>
          </div>
        )}

        {/* Usage warning banner (remaining <= 3) */}
        {showUsageWarning && (
          <div className="mb-3 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
            <MdWarning className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              {usageInfo.remaining === 1 
                ? 'You have 1 question remaining today.'
                : `You have ${usageInfo.remaining} questions remaining today.`}
            </p>
          </div>
        )}

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-end gap-4">
            <div className={`flex-1 bg-gray-50 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 transition-all ${
              usageInfo?.limitExceeded ? 'opacity-50' : ''
            }`}>
              <textarea
                ref={textareaRef}
                className={`w-full px-4 py-3 bg-transparent resize-none text-gray-700
                  border-0 outline-none
                  focus:outline-none focus-visible:outline-none
                  focus:border-transparent focus:ring-0 focus:shadow-none
                  appearance-none focus:appearance-none
                  ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                placeholder={
                  usageInfo?.limitExceeded 
                    ? "Daily limit reached. Please try again tomorrow." 
                    : "Type your message here..."
                }
                rows={1}
                onChange={onTextareaChange}
                onKeyDown={onKeyDown}
                onCompositionStart={onCompositionStart}
                onCompositionEnd={onCompositionEnd}
                disabled={isDisabled}
              />
            </div>
            <button
              className={`p-4 rounded-xl flex items-center gap-2 self-stretch transition-colors
                ${isLoading
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : usageInfo?.limitExceeded
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              onClick={isLoading ? onStop : onSubmit}
              disabled={usageInfo?.limitExceeded && !isLoading}
              aria-label={isLoading ? "Stop response" : "Send message"}
            >
              <span className="hidden sm:inline">{isLoading ? 'Stop' : 'Send'}</span>
              {isLoading ? <IoStop className="w-5 h-5"/> : <IoSend className="w-5 h-5"/>}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            Each user has a daily limit of 30 questions
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;